import React, { useState, useEffect } from "react";
import { useHistory, useLocation, withRouter } from "react-router-dom";
import {
  Grid,
  CircularProgress,
  Typography,
  Button,
  TextField,
  Box,
} from "@material-ui/core";
import { BACKEND_BASE_URL } from "../config";
import api from "../components/api";

function ResetPassword() {
  const history = useHistory();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [token, setToken] = useState("");

  useEffect(() => {
    // Get token from URL query params
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get("token");
    const autoLogin = params.get("auto_login") === "true";
    
    if (tokenParam) {
      setToken(tokenParam);
    } else {
      setError("Invalid reset link. No token provided.");
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validation
    if (!password || password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (!token) {
      setError("Invalid reset token.");
      return;
    }

    setIsLoading(true);

    try {
      const response = await api.post(`${BACKEND_BASE_URL}/api/demo/reset-password/`, {
        token: token,
        new_password: password,
        auto_login: true, // Request auto-login
      });

      if (response.data.access && response.data.refresh) {
        // Store tokens
        localStorage.setItem("authToken", response.data.access);
        localStorage.setItem("refreshToken", response.data.refresh);

        // Redirect to dashboard
        if (response.data.redirect_url) {
          window.location.href = response.data.redirect_url;
        } else {
          history.push("/app/manage");
        }
      } else {
        setSuccess(true);
        setTimeout(() => {
          history.push("/login");
        }, 2000);
      }
    } catch (err) {
      console.error("Reset password error:", err);
      setError(
        err?.response?.data?.detail ||
        err?.response?.data?.error ||
        "Failed to reset password. Please try again."
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Grid container style={{ minHeight: "100vh", backgroundColor: "#f5f5f5" }}>
      <Grid
        item
        xs={12}
        sm={8}
        md={6}
        lg={4}
        style={{
          margin: "auto",
          padding: "40px",
          backgroundColor: "white",
          borderRadius: "8px",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Typography variant="h4" style={{ marginBottom: "20px", fontWeight: "bold", color: "#0B194A" }}>
          Reset Password
        </Typography>

        {error && (
          <Box
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#ffebee",
              color: "#c62828",
              borderRadius: "4px",
              border: "1px solid #ef5350",
            }}
          >
            <Typography variant="body2">{error}</Typography>
          </Box>
        )}

        {success && (
          <Box
            style={{
              marginBottom: "20px",
              padding: "12px",
              backgroundColor: "#e8f5e9",
              color: "#2e7d32",
              borderRadius: "4px",
              border: "1px solid #4caf50",
            }}
          >
            <Typography variant="body2">Password reset successfully! Redirecting to login...</Typography>
          </Box>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="New Password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            required
            disabled={isLoading || success}
            helperText="Password must be at least 8 characters"
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            margin="normal"
            required
            disabled={isLoading || success}
          />

          <Box style={{ marginTop: "30px", display: "flex", gap: "10px" }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading || success || !password || !confirmPassword}
              fullWidth
              style={{ backgroundColor: "#0B194A" }}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : "Reset Password"}
            </Button>
          </Box>

          <Box style={{ marginTop: "20px", textAlign: "center" }}>
            <Button
              onClick={() => history.push("/login")}
              color="primary"
              disabled={isLoading}
            >
              Back to Login
            </Button>
          </Box>
        </form>
      </Grid>
    </Grid>
  );
}

export default withRouter(ResetPassword);

